import React, { useCallback, useEffect, useState } from 'react';

import { RepositoryPathStatus } from '@back4app2/sdk/lib/Back4app2';

import back4app2 from '../../back4app2';
import Button from '../Button';
import Modal from '../Modal';
import Skeleton from '../Skeleton';
import { ReactComponent as FolderIcon } from '../../assets/images/folder-icon.svg';

// Root directory paths are stored as "./" or "./api/server". These helpers are
// the single place that knows that shape, so the browser and the text input
// stay two views of the same value.
export const ROOT_PATH = './';

export function pathSegments(path: string): string[] {
  return path
    .replace(/^\.\//, '')
    .split('/')
    .filter((segment) => segment.length > 0);
}

export function buildPath(segments: string[]): string {
  return segments.length > 0 ? `./${segments.join('/')}` : ROOT_PATH;
}

interface RepositoryBrowserProps {
  open: boolean;
  repositoryId: string;
  branchName: string;
  // Where to open the browser. Browsing does not touch it — only Use this
  // folder does.
  initialPath: string;
  onClose: () => void;
  onSelect: (path: string) => void;
}

// Browsing happens inside the modal and commits only on confirm. Navigating
// straight into the form field would mean every exploratory click rewrites the
// Root Directory and re-runs the stack detection — looking around would change
// the app being created.
const RepositoryBrowser = ({
  open,
  repositoryId,
  branchName,
  initialPath,
  onClose,
  onSelect,
}: RepositoryBrowserProps) => {
  const [path, setPath] = useState(initialPath);
  const [directories, setDirectories] = useState<string[]>([]);
  const [pathStatus, setPathStatus] = useState<RepositoryPathStatus | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const segments = pathSegments(path);

  // Opening always starts from the path the form currently holds, even if the
  // user browsed elsewhere and cancelled last time.
  useEffect(() => {
    if (open) {
      setPath(initialPath);
    }
  }, [open, initialPath]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setHasFailed(false);

    try {
      const result = await back4app2.repositoryDirectories(
        repositoryId,
        branchName,
        path
      );
      setDirectories(result.directories);
      setPathStatus(result.pathStatus);
    } catch (e) {
      console.error('error listing repository directories', e);
      setDirectories([]);
      setPathStatus(undefined);
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [repositoryId, branchName, path]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-6 w-48 rounded" />
          <Skeleton className="h-6 w-40 rounded" />
          <Skeleton className="h-6 w-44 rounded" />
        </div>
      );
    }

    if (hasFailed) {
      return (
        <div className="opacity-70">
          We couldn't read this repository. Check the branch name and try again.
        </div>
      );
    }

    if (pathStatus === RepositoryPathStatus.NOT_FOUND) {
      return (
        <div className="opacity-70">
          This folder doesn't exist on {branchName}.
        </div>
      );
    }

    if (pathStatus === RepositoryPathStatus.FILE) {
      return <div className="opacity-70">This path is a file, not a folder.</div>;
    }

    if (directories.length === 0) {
      return (
        <div className="opacity-70">
          {segments.length === 0
            ? 'This repository has no subfolders — your app is at the root.'
            : 'No subfolders here.'}
        </div>
      );
    }

    return (
      <ul>
        {directories.map((directory) => (
          <li key={directory}>
            <button
              type="button"
              className="flex items-center gap-2 w-full text-left px-2 py-2 rounded hover:bg-old-blue/10"
              onClick={() => setPath(buildPath([...segments, directory]))}
            >
              <FolderIcon className="w-4 h-4 shrink-0 opacity-70" />
              {directory}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal open={open} onClose={onClose} theme="DARK" minWidth="34rem">
      <div className="text-white space-y-4">
        <div className="font-semibold">Select the root directory</div>

        {/* Breadcrumb doubles as the way back up: every crumb navigates. */}
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <button
            type="button"
            className={segments.length > 0 ? 'underline' : 'opacity-70'}
            onClick={() => setPath(ROOT_PATH)}
            disabled={segments.length === 0}
          >
            {branchName || 'root'}
          </button>
          {segments.map((segment, index) => (
            <React.Fragment key={`${segment}-${index}`}>
              <span className="opacity-40">›</span>
              <button
                type="button"
                className={index === segments.length - 1 ? 'opacity-70' : 'underline'}
                onClick={() => setPath(buildPath(segments.slice(0, index + 1)))}
                disabled={index === segments.length - 1}
              >
                {segment}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Fixed height: the list changes size at every level, and a modal that
            resizes under the cursor makes the folder you were aiming at move. */}
        <div className="h-64 overflow-y-auto text-sm border border-light-blue border-opacity-20 rounded p-2">
          {segments.length > 0 && !isLoading && (
            <button
              type="button"
              className="flex items-center gap-2 w-full text-left px-2 py-2 rounded hover:bg-old-blue/10 opacity-70"
              onClick={() => setPath(buildPath(segments.slice(0, -1)))}
            >
              ..
            </button>
          )}
          {renderBody()}
        </div>

        {/* No path echo here: the breadcrumb above already says where you are,
            and repeating it as raw text was the same information twice. */}
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => {
              onSelect(path);
              onClose();
            }}
          >
            Use this folder
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RepositoryBrowser;
